import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Check, Calendar, Clock, FileText, TrendingDown, CheckSquare, BellRing,
  Utensils, ShoppingBag, Heart, Scissors, Building2, Briefcase
} from 'lucide-react';
import { FEATURE_BLOCKS } from './landingData';

const FEATURE_LINKS = [
  { to: '/employee-scheduling',  label: 'Employee Scheduling',  Icon: Calendar,     benefit: 'Auto-build shifts in seconds'      },
  { to: '/time-tracking',        label: 'Time Tracking',        Icon: Clock,        benefit: 'Accurate clock-ins, zero disputes' },
  { to: '/timesheet-software',   label: 'Timesheet Software',   Icon: FileText,     benefit: 'Payroll-ready in one click'        },
  { to: '/labor-cost-management',label: 'Labor Cost Management',Icon: TrendingDown, benefit: 'Control costs in real time'        },
  { to: '/task-management',      label: 'Task Management',      Icon: CheckSquare,  benefit: 'Assign and track team execution'   },
  { to: '/help/notifications',   label: 'Staff Notifications',  Icon: BellRing,     benefit: 'Instant alerts for shifts and changes' },
];

const INDUSTRY_LINKS = [
  { to: '/restaurants',  label: 'Restaurants',  Icon: Utensils,    context: 'Shift-heavy teams'       },
  { to: '/retail',       label: 'Retail',        Icon: ShoppingBag, context: 'Multi-location staff'    },
  { to: '/clinics',      label: 'Clinics',       Icon: Heart,       context: 'Compliance tracking'     },
  { to: '/salons',       label: 'Salons',        Icon: Scissors,    context: 'Appointment-based teams' },
  { to: '/construction', label: 'Construction',  Icon: Building2,   context: 'Field workforce'         },
  { to: '/agencies',     label: 'Agencies',      Icon: Briefcase,   context: 'Distributed teams'       },
];

export function FeaturesSection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('cards-visible'); observer.disconnect(); } },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <section className="py-12 md:py-24 bg-white" id="features" aria-label="Features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        <div className="text-center mb-10 md:mb-20">
          <h2
            className="text-4xl md:text-5xl font-bold text-slate-900 leading-tight"
            style={{ fontFamily: 'Manrope' }}
          >
            Everything You Need to<br className="hidden sm:block" /> Manage Your Workforce
          </h2>
          <p className="mt-5 text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            A modern workforce management platform with all the tools to manage employees, schedules, and labor costs across your organization.
          </p>
        </div>

        <div className="space-y-8 md:space-y-12 lg:space-y-32">
          {FEATURE_BLOCKS.map((feature, index) => (
            <div key={feature.key}>
              <div
                className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-6 md:gap-12 lg:gap-20 items-center`}
              >
                <div className="w-full lg:w-[58%] flex-shrink-0">
                  <img
                    src={feature.img}
                    alt={feature.alt}
                    loading="lazy"
                    width="960"
                    height="620"
                    className="w-full rounded-2xl shadow-2xl border border-slate-100"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className="text-3xl md:text-4xl font-bold text-slate-900 mb-5 leading-tight"
                    style={{ fontFamily: 'Manrope' }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-slate-600 text-lg leading-relaxed mb-8">{feature.desc}</p>
                  <ul className="space-y-4">
                    {feature.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-orange-100 border border-orange-200 flex items-center justify-center flex-shrink-0">
                          <Check size={12} className="text-orange-600" strokeWidth={3} />
                        </span>
                        <span className="text-slate-800 text-base font-semibold">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              {index < FEATURE_BLOCKS.length - 1 && (
                <div className="mt-6 md:mt-12 lg:mt-32 border-t border-slate-100" />
              )}
            </div>
          ))}
        </div>

        {/* ── Feature Cards + Industry Cards ────────────────────────────── */}
        <div ref={sectionRef} className="mt-16 md:mt-24">
          <div className="bg-[#F8F9FB] rounded-3xl py-14 px-6 md:px-12">

            {/* SECTION 1: Feature Cards */}
            <div className="mb-14">
              <div className="text-center mb-10">
                <span className="text-xs font-bold uppercase tracking-widest text-orange-500">Features</span>
                <h3
                  className="text-2xl md:text-3xl font-bold text-slate-900 mt-2"
                  style={{ fontFamily: 'Manrope' }}
                >
                  Everything You Need to Run Your Workforce
                </h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {FEATURE_LINKS.map(({ to, label, Icon, benefit }, i) => (
                  <Link
                    key={to}
                    to={to}
                    className="feature-card group bg-white rounded-2xl p-4 md:p-5 border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
                    style={{ animationDelay: `${i * 70}ms` }}
                  >
                    <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center mb-3 flex-shrink-0">
                      <Icon size={18} className="text-orange-500" />
                    </div>
                    <p className="text-slate-900 font-semibold text-sm leading-tight mb-1">{label}</p>
                    <p className="text-slate-500 text-xs leading-relaxed">{benefit}</p>
                  </Link>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-slate-200 mb-12" />

            {/* SECTION 2: Industry Cards */}
            <div>
              <div className="text-center mb-8">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-400">Built For</span>
                <h3
                  className="text-xl font-bold text-slate-900 mt-2"
                  style={{ fontFamily: 'Manrope' }}
                >
                  Your Industry, Your Rules
                </h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
                {INDUSTRY_LINKS.map(({ to, label, Icon, context }, i) => (
                  <Link
                    key={to}
                    to={to}
                    className="industry-card group bg-white rounded-xl p-4 border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col items-center text-center"
                    style={{ animationDelay: `${(i + FEATURE_LINKS.length) * 70}ms` }}
                  >
                    <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center mb-2 group-hover:bg-orange-50 transition-colors">
                      <Icon size={17} className="text-slate-500 group-hover:text-orange-500 transition-colors" />
                    </div>
                    <p className="text-slate-900 font-semibold text-xs leading-tight mb-0.5">{label}</p>
                    <p className="text-slate-400 text-xs">{context}</p>
                  </Link>
                ))}
              </div>
            </div>

          </div>
        </div>

        {/* fade-in animation styles */}
        <style>{`
          .feature-card, .industry-card {
            opacity: 0;
            transform: translateY(14px);
          }
          .cards-visible .feature-card,
          .cards-visible .industry-card {
            animation: cardFadeUp 0.4s ease forwards;
          }
          @keyframes cardFadeUp {
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

      </div>
    </section>
  );
}
